import { HttpClient } from '@angular/common/http';
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  ViewRef,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicFormLayout, DynamicFormModel, DynamicFormService } from '@ng-dynamic-forms/core';
import { TranslationService } from '@spartacus/core';
import { CookieService } from 'ngx-cookie';
import { Observable, Subscription, forkJoin } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { BaseSiteConfigService } from 'src/app/service/base-site/base-site-config.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import {
  GetDynamicFormForCountry,
  ShimanoDynamicFormService,
} from '../../../service/shimano-dynamic-form/shimano-dynamic-form.service';
import { RequestAccountService } from '../../../service/user/request-account.service';
import { SHIMANO_FORM_LAYOUT } from './dynamic-forms.layout';
import { DynamicMaterialFormComponent } from './ui-material/dynamic-material-form.component';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-forms.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DynamicFormsComponent implements OnInit, AfterContentChecked, AfterViewInit, OnDestroy {
  @Input()
  formGroupId: string;

  @Input()
  static: boolean;

  @Input()
  formModel: DynamicFormModel;

  @Input()
  values: Array<any>;

  @Input()
  emitOnInit = false;

  @Input()
  formChange = false;

  @Input()
  fieldChange: string;

  @Input() activeLanguage: string;

  @ViewChild(DynamicMaterialFormComponent, { static: false }) formComponent: DynamicMaterialFormComponent;

  formGroup: FormGroup = this.fb.group({
    initial: [],
    recaptchaReactive: new FormControl(null, Validators.required),
  });
  // @todo will need to create service to return layout dynamically
  formLayout: DynamicFormLayout = SHIMANO_FORM_LAYOUT;
  @Output() formModelEmit: EventEmitter<DynamicFormModel> = new EventEmitter<DynamicFormModel>();
  @Output() formGroupEmit: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  @Output() eventFormGroupEmit: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
  countriesUser$: Observable<any>;
  languageUser$: Observable<any>;
  mobilePrefix$: Observable<any>;
  stateUser$: Observable<any>;
  errorMessage: string;
  hasRequested: string;
  recaptchaError: false;
  formError = false;
  countryChanged = false;
  langChanged = false;
  selectedLang: string;
  recaptchaSiteKey: string;
  recaptchaSubscription: Subscription;
  @Input() getLanguage: any;

  activeCountry$: Observable<string> = this.countryService.getActive();
  isRequestAccountEnabled$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) => this.requestAccountService.isRequestAccountEnabled(activeCountry))
  );

  constructor(
    private formService: DynamicFormService,
    private http: HttpClient,
    private fb: FormBuilder,
    private shimanoDynamicFormService: ShimanoDynamicFormService,
    private cookieService: CookieService,
    private requestAccountService: RequestAccountService,
    protected translation: TranslationService,
    private componentService: ComponentService,
    private languageService: ShimanoLanguageService,
    private countryService: ShimanoCountryService,
    private cdr: ChangeDetectorRef,
    private baseSiteConfigService: BaseSiteConfigService,
    private storageService: StorageService
  ) {
    this.selectedLang = this.language;
  }

  get language(): string {
    let language: string;
    this.languageService
      .getActive()
      .pipe(take(1))
      .subscribe((data) => (language = data));
    return language;
  }

  ngOnInit() {
    this.hasRequested = this.storageService.getLocalStorageItem('hasRequested');
    if (this.static) {
      this.http
        .get<any[]>(`../../../../public/forms/${this.selectedLang}/${this.formGroupId}.json`)
        .pipe(take(1))
        .subscribe((resp) => {
          if (typeof this.values !== 'undefined') {
            // populate value to the fields
            const fields = resp[0].group;
            fields.forEach((field: any) => {
              const setValue = this.values.filter((val) => val.id === field.id);
              field.value = setValue[0].value;
            });
          }

          this.formModel = this.formService.fromJSON(resp);
          this.formGroup = this.formService.createFormGroup(this.formModel);
          this.componentService.setDynamicFormGroup(this.formGroup);
        });
    } else {
      this.loadNonStaticDynamicForm();
    }

    this.recaptchaSubscription = this.getShimanoRecapthaSitekey$().subscribe((siteKey) => {
      this.recaptchaSiteKey = siteKey;
      this.cdr.detectChanges();
    });
  }

  loadNonStaticDynamicForm() {
    const country =
      this.storageService.getSessionStorageItem('countrySelected') || this.cookieService.get('userCountry');
    const language = this.storageService.getSessionStorageItem('language');
    const payload: GetDynamicFormForCountry = {
      fields: 'DEFAULT',
      countryCode: country,
    };
    this.shimanoDynamicFormService
      .getDynamicForm(this.formGroupId, payload)
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          const response = this.sanitizeToFormModel(res, language);
          this.getLanguage = language;
          this.formModel = this.formService.fromJSON(response);

          // emit formModel to component onInit
          if (this.emitOnInit) {
            this.componentService.setDynamicFormModel(this.formModel);
          }

          this.formGroup = this.formService.createFormGroup(this.formModel);

          if (this.hasRequested === 'true') {
            this.formGroup.addControl('recaptchaReactive', new FormControl(null, Validators.required));
          }

          // emit formGroup to component onInit
          if (this.emitOnInit) {
            this.componentService.setDynamicFormGroup(this.formGroup);
          }
        }
      });
  }

  sanitizeToFormModel(json: any, language?: string) {
    const group: any[] = [];
    json.fields.forEach((field: any) => {
      let customFieldType;
      let customAdditional;
      let customPlaceholder;
      let customLabel;
      let customPrefix;
      switch (field.type) {
        case 'DROP_DOWN':
          customFieldType = 'SELECT';
          customAdditional = {
            type: field.uid,
            defaultValue: 'SO',
            optionClass: 'js-order-type',
          };
          break;
        case 'DATE':
          customFieldType = 'DATEPICKER';
          break;
        case 'EMAIL':
          customFieldType = 'INPUT';
          customPrefix = this.getTranslatedValue(field.label, field.localizedLabels, language);
          break;
        case 'RADIO':
          customFieldType = 'RADIO_GROUP';
          break;
        case 'TEXT':
          customFieldType = 'INPUT';
          customPrefix = this.getTranslatedValue(field.label, field.localizedLabels, language);
          break;
        case 'TEXT_AREA':
          customFieldType = 'TEXTAREA';
          customPrefix = this.getTranslatedValue(field.label, field.localizedLabels, language);
          break;
        case 'TEL':
          customFieldType = 'INPUT';
          customPrefix = this.getTranslatedValue(field.label, field.localizedLabels, language);
          break;
        case 'CHECKBOX':
          customFieldType = 'CHECKBOX';
          customAdditional = {
            checkboxDesc: this.getTranslatedValue(field.description, field.localizedDescriptions, language),
          };
          break;
        default:
          return;
      }
      const options: any[] = [];
      if (field.items) {
        field.items.forEach((option: any) => {
          const o = {
            label: this.getTranslatedValue(option.name, option.localizedNames, language),
            value: option.code,
          };
          options.push(o);
        });
      }
      this.translation
        .translate(`error.dynamicform.${field.uid}`)
        .pipe(take(1))
        .subscribe((errorText) => {
          this.errorMessage = errorText;
        });
      const pattern = {
        pattern: field.regex,
        errorMessages: {
          pattern: this.errorMessage,
        },
      };
      const dynamicformfield = {
        id: field.uid,
        type: customFieldType,
        placeholder:
          field.label && field.localizedLabels
            ? this.getTranslatedValue(field.label, field.localizedLabels, language)
            : field.placeholder && field.localizedPlaceholders
            ? this.getTranslatedValue(field.placeholder, field.localizedPlaceholders, language)
            : null,
        label:
          field.placeholder && field.localizedPlaceholders
            ? this.getTranslatedValue(field.placeholder, field.localizedPlaceholders, language)
            : field.label && field.localizedLabels
            ? this.getTranslatedValue(field.label, field.localizedLabels, language)
            : null,
        prefix: customPrefix || field.prefix,
        required: field.mandatory,
        options,
        additional: customAdditional,
        ...pattern,
      };
      group.push(dynamicformfield);
    });
    const formArray = group;
    return formArray;
  }
  onBlur($event: any) {}

  onChange(val: any) {
    const field = val.value[this.fieldChange];
    if (
      field !== this.storageService.getSessionStorageItem('fieldChange') &&
      this.formChange &&
      typeof field !== 'undefined' &&
      field !== null
    ) {
      this.storageService.setSessionStorageItem('fieldChange', field);
      val.value.state = null; // set state to null
      this.storageService.setSessionStorageItem('user_input_values', JSON.stringify(val.value));
      this.changeDynamicFormBasedonCountry(field);
    } else {
      this.formGroupEmit.emit(val);
      this.formModelEmit.emit(this.formModel);
    }
  }

  changeDynamicFormBasedonCountry(field: string) {
    const country = field;
    const payload: GetDynamicFormForCountry = {
      fields: 'DEFAULT',
      countryCode: country,
    };
    const getDynamicFormForCountry = this.shimanoDynamicFormService.getDynamicFormForCountry(this.formGroupId, payload);
    const getRegions = this.shimanoDynamicFormService.getRegions(country);

    forkJoin([getDynamicFormForCountry, getRegions])
      .pipe(take(1))
      .subscribe((res) => {
        const formbyCountryResult = res[0];
        const formRegions = res[1];
        if (formbyCountryResult) {
          if (formRegions.items.length > 0 && formbyCountryResult.fields.region) {
            // IF REGION HAS ITEMS
            formbyCountryResult.fields.filter((attr: any) => attr.attribute === 'region')[0].items = formRegions.items;
          }
          const response = this.sanitizeToFormModel(formbyCountryResult);
          this.formError = false;
          this.formGroup = this.fb.group({
            initial: [],
          });
          this.formModel = this.formService.fromJSON(response);
          this.updateFormModel(this.formModel);
          this.formGroup = this.formService.createFormGroup(this.formModel);
          if (this.formError) {
            this.formGroup.setErrors({ val: true });
          }
          this.formGroupEmit.emit(this.formGroup);
          this.formModelEmit.emit(this.formModel);
        }
      });
  }

  updateFormModel(form: any) {
    if (form.length > 0) {
      const fm = form;
      const userValues = JSON.parse(this.storageService.getSessionStorageItem('user_input_values'));
      form.forEach((field: any) => {
        const prop = this.formService.findModelById<any>(field.id, fm);
        if (prop !== null) {
          prop.value = userValues[field.id] || '';
          if (field.required && (prop.value === null || prop.value === '')) {
            this.formError = true;
          }
        }
      });
    }
  }

  onFocus($event: any) {}

  onMatEvent($event: any) {}

  public resolved(captchaResponse: string) {
    if (captchaResponse !== null) {
      this.recaptchaError = false;
    }
  }

  getTranslatedValue(fallbackValue: string, translatedValues: any, language: string): string {
    let translatedValue = fallbackValue;
    if (translatedValues && translatedValues.entry && translatedValues.entry.length) {
      const filteredTranslation = translatedValues.entry.filter((v: any) => v.key === language);
      if (filteredTranslation.length) {
        translatedValue = filteredTranslation[0].value;
      }
    }
    return translatedValue;
  }

  getShimanoRecapthaSitekey$(): Observable<string> {
    const shimanoRecaptchaSitekey = 'shimano.recaptcha.sitekey';
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$([shimanoRecaptchaSitekey])
      .pipe(
        map(
          (configs) =>
            configs &&
            configs.filter((config: any) => config.key === shimanoRecaptchaSitekey).map((res: any) => res.value)[0]
        )
      );
  }

  ngAfterViewInit() {
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
  }
  ngAfterContentChecked() {
    if (!(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }
  }

  ngOnChanges(changes: any) {
    if (changes.activeLanguage && !changes.activeLanguage.firstChange) {
      this.loadNonStaticDynamicForm();
    }
  }

  ngOnDestroy() {
    if (this.recaptchaSubscription) {
      this.recaptchaSubscription.unsubscribe();
    }
  }
}
